import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  format,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Task } from '../../types';
import { Badge, getStatusVariant, statusLabels } from '../common/Badge';
import { TaskPeople } from './TaskPeople';

interface TaskCalendarViewProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onAddTask: (status?: Task['status']) => void;
}

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function TaskCalendarView({ tasks, onTaskClick, onAddTask }: TaskCalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const getTasksForDay = (day: Date) =>
    tasks.filter(task => task.due_date && isSameDay(new Date(task.due_date), day));

  const selectedDayTasks = selectedDay ? getTasksForDay(selectedDay) : [];

  return (
    <div className="flex gap-6 flex-col lg:flex-row">
      {/* Calendar grid */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Month navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <button
            onClick={prevMonth}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <h2 className="text-base font-semibold text-gray-900 capitalize">
            {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
          </h2>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {weekDays.map(day => (
            <div key={day} className="py-2 text-center text-xs font-semibold text-gray-400 uppercase">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, idx) => {
            const dayTasks = getTasksForDay(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = selectedDay ? isSameDay(day, selectedDay) : false;
            const isTodayDate = isToday(day);

            return (
              <div
                key={idx}
                onClick={() => setSelectedDay(selectedDay && isSameDay(day, selectedDay) ? null : day)}
                className={`min-h-[90px] border-b border-r border-gray-100 p-1.5 cursor-pointer transition-colors
                  ${!isCurrentMonth ? 'bg-gray-50' : 'bg-white hover:bg-primary-50/30'}
                  ${isSelected ? 'bg-primary-50 ring-1 ring-inset ring-primary-200' : ''}
                `}
              >
                {/* Day number */}
                <div className="flex justify-end mb-1">
                  <span
                    className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
                      ${isTodayDate ? 'bg-primary-600 text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}
                    `}
                  >
                    {format(day, 'd')}
                  </span>
                </div>

                {/* Tasks for day (max 2 visible) */}
                <div className="space-y-0.5">
                  {dayTasks.slice(0, 2).map(task => (
                    <div
                      key={task.id}
                      onClick={e => { e.stopPropagation(); onTaskClick(task); }}
                      className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer font-medium
                        ${task.status === 'done' ? 'bg-green-100 text-green-700' :
                          task.status === 'in_progress' ? 'bg-primary-100 text-primary-700' :
                          task.status === 'in_review' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'}
                      `}
                      title={task.title}
                    >
                      {task.title}
                    </div>
                  ))}
                  {dayTasks.length > 2 && (
                    <div className="text-xs text-gray-400 px-1.5">
                      +{dayTasks.length - 2} mais
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day detail panel */}
      <div className="lg:w-72 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {selectedDay
              ? format(selectedDay, "dd 'de' MMMM", { locale: ptBR })
              : 'Selecione um dia'}
          </h3>
          {selectedDay && (
            <button
              onClick={() => onAddTask('todo')}
              className="p-1 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors"
              title="Adicionar tarefa"
            >
              <Plus size={16} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!selectedDay && (
            <p className="text-sm text-gray-400 text-center mt-8">
              Clique em um dia para ver as tarefas
            </p>
          )}

          {selectedDay && selectedDayTasks.length === 0 && (
            <div className="text-center mt-8 space-y-2">
              <p className="text-sm text-gray-400">Nenhuma tarefa com prazo neste dia</p>
              <button
                onClick={() => onAddTask('todo')}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 mx-auto"
              >
                <Plus size={14} />
                Adicionar tarefa
              </button>
            </div>
          )}

          {selectedDay && selectedDayTasks.length > 0 && (
            <div className="space-y-3">
              {selectedDayTasks.map(task => (
                <div
                  key={task.id}
                  onClick={() => onTaskClick(task)}
                  className="p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm cursor-pointer transition-all"
                >
                  <p className="text-sm font-medium text-gray-900 mb-1.5">{task.title}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={getStatusVariant(task.status)}>
                      {statusLabels[task.status] || task.status}
                    </Badge>
                    <TaskPeople task={task} showNames />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tasks without due dates summary */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-400">
            {tasks.filter(t => !t.due_date).length} tarefas sem prazo definido
          </p>
        </div>
      </div>
    </div>
  );
}
